import { AHAnimatedPageIndicator, Box } from "arkh";
import Animated, {
  Extrapolate,
  interpolate,
  runOnJS,
  useAnimatedReaction,
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
} from "react-native-reanimated";
import { FlatList, Platform } from "react-native";
import React, { useCallback, useMemo, useRef } from "react";

import { AddPage } from "./AddPage";
import { LastItemComponent } from "./AddPageDotIndicator";
import { PageWrapper } from "./PageWrapper";

import { WidgetNotePage } from "./WidgetNotePage";

import { useNoteAddPage } from "./useAddNotePage";

import { useWidgetLayout } from "../WidgetLayoutContext";

import { graphql, useFragment } from "react-relay";
import { pageForegroundColor } from "./styleHelpers/pageForegroundColor";
import { NoteNative_note$key } from "./__generated__/NoteNative_note.graphql";
import { useNoteSubscriptions } from "./useNoteSubscriptions";
import { WidgetNoteTitle } from "./WidgetNoteTitle";

const AnimatedFlatList = Animated.createAnimatedComponent(FlatList) as any;

export function NoteNative(props: {
  note: NoteNative_note$key;
  clientTitle?: string;
  interactive?: false;
  onPageChange?: (pageId: string) => void;
  onPagePress?: (pageId: string) => void;
  isRenderedFromOrg?: boolean;
}) {
  const note = useFragment(
    graphql`
      fragment NoteNative_note on Note {
        id
        editingMode
        isOrgNote
        title
        pages {
          id
          image {
            id
          }
          backgroundColor
          ...WidgetNotePage_widgetNotePage
        }
      }
    `,
    props.note
  );
  const locked = note.isOrgNote && !props.isRenderedFromOrg;

  const { height, width } = useWidgetLayout();
  useNoteSubscriptions(note.id, props.interactive === false);
  const y = useSharedValue(0);
  const scrollRef = useRef<FlatList>(null);

  const _title = note.title;
  const pages = note.pages;
  const title = props.clientTitle ?? _title;

  const canAddPage =
    !locked && note.editingMode === "DEFAULT" && props.interactive !== false;
  const data = useMemo(
    () =>
      canAddPage
        ? [...pages, "ADD" as const]
        : props.interactive === false
        ? pages.slice(0, 1)
        : pages,
    [canAddPage, props.interactive, pages]
  );
  type PageType = (typeof data)[0];

  const scrollHandler = useAnimatedScrollHandler({
    onScroll: (e) => {
      y.value = e.contentOffset.y;
    },
  });

  const currentIndex = useDerivedValue(
    () => Math.max(0, Math.round(y.value / height)),
    [y, height]
  );

  const opc = props.onPageChange;

  const onScrollToNewIndex = useCallback(
    (index: number) => {
      const id = pages[index]?.id;
      if (id) {
        opc?.(pages[index]?.id);
      }
    },
    [opc, pages]
  );
  useAnimatedReaction(
    () => currentIndex.value,
    (current, prev) => {
      if (current != null && current !== prev) {
        runOnJS(onScrollToNewIndex)(current);
      }
    }
  );

  const [addPage, addPageLoading] = useNoteAddPage();
  const inputRange = useMemo(
    () => data.map((_, i) => i * height),
    [data, height]
  );

  const foregroundColors = useMemo(
    () => data.map((page) => pageForegroundColor(page)),
    [data]
  );

  const outputDarkTitle = useMemo(
    () => foregroundColors.map((c) => (c === "DARK" ? 1 : 0)),
    [foregroundColors]
  );
  const outputLightTitle = useMemo(
    () => foregroundColors.map((c) => (c === "LIGHT" ? 1 : 0)),
    [foregroundColors]
  );

  const darkTitleOpacity = useAnimatedStyle(() => {
    "worklet";
    return {
      opacity:
        inputRange.length > 1
          ? interpolate(
              y.value,
              inputRange,
              outputDarkTitle,
              Extrapolate.CLAMP
            ) ?? 0
          : outputDarkTitle[0],
    };
  });
  const lightTitleOpacity = useAnimatedStyle(() => {
    "worklet";
    return {
      opacity:
        inputRange.length > 1
          ? interpolate(
              y.value,
              inputRange,
              outputLightTitle,
              Extrapolate.CLAMP
            ) ?? 0
          : outputLightTitle[0],
    };
  });
  const p = useDerivedValue(() => y.value / height);

  const renderItem = useCallback(
    ({ item, index }: { item: PageType; index: number }) =>
      item === "ADD" ? (
        <AddPage
          width={width}
          height={height}
          onAddPage={
            addPageLoading
              ? () => {}
              : () =>
                  addPage(note.id, index, (res) => {
                    props.onPagePress?.(
                      res.note.pageCreate.pages[
                        res.note.pageCreate.pages.length - 1
                      ].id
                    );
                  })
          }
        />
      ) : (
        <PageWrapper
          onPress={() => {
            props.onPagePress?.(item.id);
          }}
          waitFor={Platform.OS === "web" ? undefined : scrollRef}
          y={y}
          length={data.length}
          index={index}
          key={`${item.id}${index}`}
        >
          <WidgetNotePage page={item} />
        </PageWrapper>
      ),
    [width, height, addPageLoading, y, data.length, addPage, note.id, props]
  );
  const keyExtractor = useCallback(
    (item: PageType) => (item === "ADD" ? "ADD" : item.id),
    []
  );

  const getItemLayout = useCallback(
    (_: unknown, index: number) => ({
      length: height,
      offset: height * index,
      index,
    }),
    [height]
  );

  return (
    <>
      <AnimatedFlatList
        ref={scrollRef}
        getItemLayout={getItemLayout}
        onScroll={scrollHandler}
        showsVerticalScrollIndicator={false}
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        bounces={false}
        data={data}
        scrollEventThrottle={16}
        scrollEnabled={data.length > 1}
        keyExtractor={keyExtractor}
        renderItem={renderItem}
        maxToRenderPerBatch={Platform.OS === "web" ? undefined : 2}
        initialNumToRender={Platform.OS === "web" ? undefined : 2}
        windowSize={Platform.OS === "web" ? undefined : 2}
      />
      <Animated.View
        pointerEvents="none"
        style={[
          { position: "absolute", left: 0, top: 0, right: 0 },
          darkTitleOpacity,
        ]}
      >
        <WidgetNoteTitle
          textColor="darkerText"
          enableTextShadow={false}
          title={title}
          locked={locked}
        />
      </Animated.View>
      <Animated.View
        pointerEvents="none"
        style={[
          { position: "absolute", left: 0, top: 0, right: 0 },
          lightTitleOpacity,
        ]}
      >
        <WidgetNoteTitle
          textColor="lightText"
          enableTextShadow
          title={title}
          locked={locked}
        />
      </Animated.View>
      {data.length > 1 ? (
        <Box
          pointerEvents="none"
          style={{
            position: "absolute",
            right: 0,
            top: 0,
            height: "100%",
            justifyContent: "center",
          }}
          spaceHorizontal="small"
          spaceTop="xsmall"
        >
          <AHAnimatedPageIndicator
            direction="vertical"
            page={p}
            numberOfItems={data.length}
            LastItemComponent={canAddPage ? LastItemComponent : undefined}
          />
        </Box>
      ) : null}
    </>
  );
}
